import React from "react";
import { Download, Loader2 } from "lucide-react";
import {
  formatOddsDisplayValue,
  formatOutcomeLabel,
  isLeafOddsValue,
  ODDS_FIELD_LABELS,
  validateOddsValue,
} from "../../utils/oddsCustomizeHelpers";

const isEmptyDisplay = (value) =>
  value === "" || value === null || value === undefined || value === 0;

const OddsFieldInput = ({ fieldPath, value, error, onChange, onBlur }) => {
  const validation = validateOddsValue(value);
  const invalid = Boolean(error) || (!validation.valid && !isEmptyDisplay(value));
  const filled = !isEmptyDisplay(value);

  return (
    <label
      className={[
        "oc-odds-field",
        invalid ? "oc-odds-field-invalid" : "",
        filled ? "oc-odds-field-filled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="oc-odds-field-label">{formatOutcomeLabel(fieldPath.split(".").pop())}</span>
      <input
        type="text"
        inputMode="decimal"
        className="oc-odds-input tabular-nums"
        value={formatOddsDisplayValue(value)}
        placeholder="—"
        aria-invalid={invalid}
        onChange={(e) => onChange(fieldPath, e.target.value)}
        onBlur={() => onBlur(fieldPath)}
      />
      {error ? <em className="oc-odds-field-error">{error}</em> : null}
    </label>
  );
};

const OddsMarketSection = ({
  sectionTitle,
  sectionData,
  validationErrors,
  autofilledMeta,
  canAutofill,
  autofillLoading,
  onAutofill,
  onChange,
  onBlur,
}) => {
  const sectionLabel = ODDS_FIELD_LABELS[sectionTitle] || sectionTitle;
  const autofillBadge = autofilledMeta?.bookmaker?.displayName;

  const nestedEntries = [];
  const flatEntries = [];
  for (const [subTitle, values] of Object.entries(sectionData)) {
    if (typeof values === "object" && values !== null && !isLeafOddsValue(values)) {
      nestedEntries.push([subTitle, values]);
    } else {
      flatEntries.push([subTitle, values]);
    }
  }

  return (
    <section className="oc-odds-section">
      <header className="oc-odds-section-head">
        <div className="oc-odds-section-title">
          <h3>{sectionLabel}</h3>
          {autofillBadge ? (
            <span className="oc-odds-autofill-badge">{autofillBadge}</span>
          ) : null}
        </div>
        {canAutofill ? (
          <button
            type="button"
            className="oc-odds-autofill-btn"
            disabled={autofillLoading}
            onClick={() => onAutofill(sectionTitle)}
            title={`Autofill ${sectionLabel}`}
          >
            {autofillLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Download size={12} />
            )}
            <span>Autofill</span>
          </button>
        ) : null}
      </header>

      <div className="oc-odds-section-body">
        {flatEntries.length ? (
          <div className="oc-odds-grid">
            {flatEntries.map(([key, value]) => {
              const fieldPath = `${sectionTitle}.${key}`;
              return (
                <OddsFieldInput
                  key={fieldPath}
                  fieldPath={fieldPath}
                  value={value}
                  error={validationErrors[fieldPath]}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              );
            })}
          </div>
        ) : null}

        {nestedEntries.map(([subTitle, values]) => {
          const subLabel = ODDS_FIELD_LABELS[subTitle] || formatOutcomeLabel(subTitle);
          return (
            <div key={subTitle} className="oc-odds-subsection">
              <div className="oc-odds-subsection-head">
                <span>{subLabel}</span>
              </div>
              <div className="oc-odds-grid">
                {Object.entries(values).map(([key, value]) => {
                  const fieldPath = `${sectionTitle}.${subTitle}.${key}`;
                  return (
                    <OddsFieldInput
                      key={fieldPath}
                      fieldPath={fieldPath}
                      value={value}
                      error={validationErrors[fieldPath]}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default OddsMarketSection;
